// TEMPORÁRIO — mock local do Portal do Credenciado, remover no desenvolvimento do backend.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { paginar, type Pagina } from '@/modulos/portal-credenciado/comum/contrato';
import { resolverMock } from '@/modulos/portal-credenciado/comum/mock';
import { normalizarBusca } from '@/nucleo/utilitarios';

import type { CobrancaAdministrativa, FiltroDeCobrancas } from './contrato';
import dadosMock from './dados.mock.json';

/**
 * Camada de dados das Cobranças Administrativas do Portal do Credenciado.
 *
 * Mesmo desenho das demais telas do módulo: nenhum componente busca dado
 * direto, a chave de cache mora junto de quem a usa, e o `queryFn` resolve
 * `dados.mock.json` em vez de chamar `chamarApi('/credenciado/...')` — ver a
 * justificativa em `comum/mock.ts`.
 */
export const CHAVES_DE_COBRANCAS = {
  cobrancas: (filtro: FiltroDeCobrancas) => ['credenciado', 'cobrancas', filtro] as const,
};

const COBRANCAS_MOCK = dadosMock.cobrancas as CobrancaAdministrativa[];

function aplicarFiltro(
  itens: CobrancaAdministrativa[],
  filtro: FiltroDeCobrancas,
): CobrancaAdministrativa[] {
  return itens.filter((cobranca) => {
    if (filtro.status && cobranca.status !== filtro.status) {
      return false;
    }

    if (filtro.tipoServico && cobranca.tipoServico !== filtro.tipoServico) {
      return false;
    }

    if (filtro.vencimentoDe && cobranca.vencimento < filtro.vencimentoDe) {
      return false;
    }

    if (filtro.vencimentoAte && cobranca.vencimento > filtro.vencimentoAte) {
      return false;
    }

    if (filtro.busca) {
      const alvo = normalizarBusca(filtro.busca);
      const combinaPorMotivo = normalizarBusca(cobranca.motivo).includes(alvo);
      const combinaPorNumero = normalizarBusca(cobranca.numero).includes(alvo);
      if (!combinaPorMotivo && !combinaPorNumero) {
        return false;
      }
    }

    return true;
  });
}

/**
 * A listagem de cobranças, paginada no mesmo formato que a API real vai
 * devolver (`comum/contrato.ts`). O filtro acontece antes da paginação —
 * igual a um `WHERE` que roda antes do `LIMIT/OFFSET` no servidor.
 */
export function useCobrancas(
  filtro: FiltroDeCobrancas,
): UseQueryResult<Pagina<CobrancaAdministrativa>, Error> {
  return useQuery<Pagina<CobrancaAdministrativa>, Error>({
    queryKey: CHAVES_DE_COBRANCAS.cobrancas(filtro),
    queryFn: () =>
      resolverMock(paginar(aplicarFiltro(COBRANCAS_MOCK, filtro), filtro.pagina, filtro.tamanho)),
    placeholderData: (anterior) => anterior,
  });
}
